import * as React from "react";

// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { Button } from "@fluentui/react";

import { dataUniqueId, InputMethod } from "@1js/acui-common";
import { AppContext } from "@1js/acui-context";
import { checkExperiment, ribbonExperiments } from "@1js/acui-experiments";
import { iconNameToIconProps } from "@1js/acui-icons";
import { defaultKeytipExecute } from "@1js/acui-keytip";
import { AppTooltipHost, getTooltipText } from "@1js/acui-tooltip";

import { deriveButtonKeytip } from "@1js/acui-keytip";
import { AppBaseButton, getLabelButtonProps } from "../AppBaseButton";
import type { AppToggleButtonInternalProps } from "./AppToggleButton.Props";

export class AppToggleButton extends React.PureComponent<
  AppToggleButtonInternalProps,
  {}
> {
  private baseButton = React.createRef<AppBaseButton>();
  private tooltipHost: AppTooltipHost | undefined;

  // This allows us to access AppContext directly from this.context
  static contextType: any = AppContext;
  declare context: React.ContextType<typeof AppContext>;

  public render() {
    return (
      <AppBaseButton
        id={this.props.id}
        ref={this.baseButton}
        styles={this.props.styles}
        onClick={this.onExecute}
        onDoubleClick={this.handleDoubleClick}
        checked={this.props.toggled}
        toggle={true}
        disabled={this.props.disabled}
        iconProps={iconNameToIconProps(
          this.props.icon,
          this.props.iconColor,
          this.props.toggleUsingIcon && !this.props.toggled
        )}
        {...getLabelButtonProps(this.props.label, this.props.hideLabel)}
        data-automation-type={
          this.props.dataAutomationType || "AppToggleButton"
        }
        data-unique-id={dataUniqueId(this.props)}
        role={this.props.role}
        title={this.props.title}
        onFocus={this.onFocus}
        keytipProps={deriveButtonKeytip(
          this.props.keytipProps,
          this.keytipExecute
        )}
        ariaLabel={this.props.label}
        ariaDescription={this.props.ariaDescription || this.props.customTooltip}
        onRenderText={this.props.onRenderText}
        data-nav={this.props.dataNav}
        aria-keyshortcuts={this.props.keyShortcuts}
      />
    );
  }

  public componentDidMount() {
    if (this.context.setTooltipElement && this.context.updateTooltipsHidden) {
      this.tooltipHost = new AppTooltipHost({
        updateTooltipsHidden: this.context.updateTooltipsHidden,
        setTooltipElement: this.context.setTooltipElement,
        htmlElement: this.baseButton.current?.buttonElement,
        controlId: this.props.id,
        controlLabel: getTooltipText(
          this.props.hideLabel,
          this.props.label,
          this.props.customTooltip
        ),
        tooltipDirection: this.props.tooltipDirection,
        performSetOnNextFrame: true,
        targetOverflowSetItem: !!checkExperiment(
          this.context,
          ribbonExperiments.tooltipsTargetOverflowSetItemEnabled
        ),
        location: this.props.location,
      });
      this.tooltipHost.registerTooltipHostActions();
    }
  }

  public componentWillUnmount() {
    if (this.tooltipHost) {
      this.tooltipHost.unregisterTooltipHostActions();
      this.tooltipHost = undefined;
    }
  }

  public componentDidUpdate(prevProps: AppToggleButtonInternalProps) {
    if (
      this.tooltipHost &&
      (prevProps.customTooltip !== this.props.customTooltip ||
        prevProps.label !== this.props.label ||
        prevProps.hideLabel !== this.props.hideLabel)
    ) {
      this.tooltipHost.updateTooltip(
        getTooltipText(
          this.props.hideLabel,
          this.props.label,
          this.props.customTooltip
        )
      );
    }
  }

  private keytipExecute = (executeTarget: HTMLElement) => {
    defaultKeytipExecute(executeTarget, this.executeFromKeytip);
  };

  private handleDoubleClick = (ev: React.MouseEvent<Button>): void => {
    if (this.props.isDoubleClickEnabled) {
      this.onExecute(ev, true /* isDoubleClick */);
    }
  };

  private onExecute = (
    ev: React.MouseEvent<Button>,
    isDoubleClick?: boolean
  ) => {
    if (this.props.onExecute) {
      const { id, toggled, location, shouldTakeFocus } = this.props;
      this.props.onExecute({
        id,
        toggled,
        location,
        inputMethod: this.baseButton?.current?.getInputMethod(ev),
        shouldTakeFocus,
        triggerTime: ev?.timeStamp,
        event: ev?.nativeEvent,
        isDoubleClick,
      });
    }
  };

  private executeFromKeytip = () => {
    if (this.props.onExecute) {
      const { id, toggled, location, shouldTakeFocus } = this.props;
      this.props.onExecute({
        id,
        toggled,
        location,
        inputMethod: InputMethod.Keyboard,
        shouldTakeFocus,
      });
    }
  };

  private onFocus = (event: React.FocusEvent<HTMLButtonElement>) => {
    this.props?.onFocus?.(event);
  };
}
